import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { FiChevronDown } from 'react-icons/fi'
import {
  createShipment,
  createShipmentsBulk,
  SHIPMENT_STATUS_OPTIONS,
  INTEGRATED_SHIPPING_LINE_OPTIONS,
  populateFromContracts,
} from '@/services/api/shipments'
import { uploadShipmentDocument } from '@/services/api/documents'
import ProductSelectInput from '@/components/products/ProductSelectInput'
import CustomerSelectInput from '@/components/customers/CustomerSelectInput'
import StockReferenceSelectInput from '@/components/inventory/StockReferenceSelectInput'
import { getContracts, Contract } from '@/services/api/contracts'

interface NewShipmentModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

const NewShipmentModal = ({ isOpen, onClose, onSuccess }: NewShipmentModalProps) => {
  const [formData, setFormData] = useState({
    buyContractId: '',
    saleContractId: '',
    buyContract: '',
    saleContract: '',
    booking: '',
    billOfLading: '',
    containerNumber: '',
    vessel: '',
    shippingLine: '',
    product: '',
    customer: '',
    factory: '',
    origin: '',
    destination: '',
    etd: '',
    eta: '',
    status: '',
    documentStatus: ''
  })
  const [buyContracts, setBuyContracts] = useState<Contract[]>([])
  const [saleContracts, setSaleContracts] = useState<Contract[]>([])
  const [shippingDocumentFile, setShippingDocumentFile] = useState<File | null>(null)
  const shippingFileInputRef = useRef<HTMLInputElement>(null)
  const [multiContainerMode, setMultiContainerMode] = useState(false)
  const [containerCount, setContainerCount] = useState(2)
  const [containerNumbers, setContainerNumbers] = useState<string[]>(['', ''])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const updateContainerCount = (count: number) => {
    const n = Math.max(2, Math.min(30, count))
    setContainerCount(n)
    setContainerNumbers((prev) => {
      if (prev.length === n) return prev
      if (prev.length < n) return [...prev, ...Array(n - prev.length).fill('')]
      return prev.slice(0, n)
    })
  }

  const handleContainerNumberChange = (index: number, value: string) => {
    setContainerNumbers((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
    setSubmitError(null)
  }

  useEffect(() => {
    if (!isOpen) return
    getContracts({ type: 'buy', limit: 100 }).then(r => setBuyContracts(r.contracts)).catch(() => {})
    getContracts({ type: 'sale', limit: 100 }).then(r => setSaleContracts(r.contracts)).catch(() => {})
  }, [isOpen])

  const handleContractChange = async (field: 'buyContractId' | 'saleContractId', value: string) => {
    const updated = { ...formData, [field]: value }
    setFormData(updated)
    setSubmitError(null)

    if (!updated.buyContractId && !updated.saleContractId) return
    try {
      const populated = await populateFromContracts({
        buyContractId: updated.buyContractId || undefined,
        saleContractId: updated.saleContractId || undefined,
      })
      setFormData(prev => ({
        ...prev,
        [field]: value,
        buyContract: populated.buyContract || prev.buyContract,
        saleContract: populated.saleContract || prev.saleContract,
        customer: populated.customer || prev.customer,
        factory: populated.factory || prev.factory,
        origin: populated.pol || prev.origin,
        destination: populated.pod || prev.destination,
        booking: populated.booking || prev.booking,
      }))
    } catch {
      // Non-critical: user can still fill manually
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value
    })
    setSubmitError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    const booking = formData.booking.trim()
    const containerNumber = formData.containerNumber.trim()
    const bl = formData.billOfLading.trim()
    const carrier = formData.shippingLine.trim()

    if (multiContainerMode) {
      if (!booking) {
        setSubmitError('Booking is required when creating multiple containers.')
        return
      }
      const numbers = containerNumbers.map((n) => n.trim())
      if (numbers.some((n) => !n)) {
        setSubmitError('Enter a container number for each container.')
        return
      }
      const unique = new Set(numbers.map((n) => n.toUpperCase()))
      if (unique.size !== numbers.length) {
        setSubmitError('Container numbers must be unique.')
        return
      }
      if (!carrier) {
        setSubmitError(
          'Shipping line is required for multi-container booking. Choose one of the integrated carriers (Hapag-Lloyd, Maersk, MSC, DSV).'
        )
        return
      }
    } else {
      const hasRef = !!(booking || containerNumber || bl)
      if (!hasRef) {
        setSubmitError('At least one of Booking number, Container number, or Bill of Lading is required (for tracking).')
        return
      }
      if (hasRef && !carrier) {
        setSubmitError(
          'Shipping line is required when you enter a booking, container, or B/L. Choose one of the integrated carriers (Hapag-Lloyd, Maersk, MSC, DSV).'
        )
        return
      }
    }

    setSubmitting(true)
    try {
      const sharedPayload = {
        buyContractId: formData.buyContractId || undefined,
        saleContractId: formData.saleContractId || undefined,
        buyContract: formData.buyContract.trim() || undefined,
        saleContract: formData.saleContract.trim() || undefined,
        booking: booking || undefined,
        bl: bl || undefined,
        vessel: formData.vessel.trim() || undefined,
        carrier: formData.shippingLine.trim() || undefined,
        product: formData.product.trim() || undefined,
        customer: formData.customer.trim() || undefined,
        factory: formData.factory.trim() || undefined,
        pol: formData.origin || undefined,
        pod: formData.destination || undefined,
        etd: formData.etd || undefined,
        eta: formData.eta || undefined,
        status: (formData.status || 'pending').toLowerCase(),
      }

      let firstShipmentId: string | undefined

      if (multiContainerMode) {
        const bulkResult = await createShipmentsBulk({
          ...sharedPayload,
          containerNumbers: containerNumbers.map((n) => n.trim()),
        })
        firstShipmentId = bulkResult.shipments[0]?.id
      } else {
        const newShipment = await createShipment({
          ...sharedPayload,
          containerNumber: containerNumber || undefined,
        })
        firstShipmentId = newShipment.id
      }

      if (shippingDocumentFile && firstShipmentId) {
        try {
          await uploadShipmentDocument(firstShipmentId, shippingDocumentFile)
        } catch (uploadErr: unknown) {
          const msg = (uploadErr as Error)?.message ?? 'Document upload failed'
          const noun = multiContainerMode ? 'Shipments created' : 'Shipment created'
          setSubmitError(`${noun} but ${msg}. You can add the document later from Shipment Details.`)
          setSubmitting(false)
          return
        }
      }

      onSuccess?.()
      onClose()
    } catch (err: any) {
      const errBody = err?.response?.data?.error
      const serverMessage =
        errBody?.details?.internalMessage ||
        errBody?.message ||
        err?.response?.data?.message
      if (err?.response?.status === 500) {
        console.error('[NewShipmentModal] Server 500:', serverMessage)
        console.error('[NewShipmentModal] Full response:', err?.response?.data)
      }
      const message =
        serverMessage ||
        err?.message ||
        'Failed to create shipment.'
      setSubmitError(message)
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      style={{ zIndex: 60 }}
    >
      <div 
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '548px',
          maxWidth: '548px',
          height: '100vh',
          maxHeight: '100vh',
          zIndex: 70,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0 px-4 sm:px-6"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2'
          }}
        >
          <h2 
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '26px',
              letterSpacing: '0px',
              color: '#212126'
            }}
          >
            New Shipment
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{ 
              width: '24px',
              height: '24px'
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="10.5"
                stroke="#212126"
                strokeWidth="1.5"
                fill="none"
              />
              <line
                x1="9.17"
                y1="9.17"
                x2="14.83"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <line
                x1="14.83"
                y1="9.17"
                x2="9.17"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form
          id="new-shipment-form"
          onSubmit={handleSubmit}
          className="p-4 sm:p-6 flex-1 scrollbar-hide"
          style={{ overflowY: 'auto', minHeight: 0 }}
        >
          {submitError && (
            <div
              className="mb-4 px-3 py-2 rounded"
              style={{ backgroundColor: '#FEE2E2', color: '#B91C1C', fontFamily: 'Lexend Deca', fontSize: '12px' }}
            >
              {submitError}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Buy Contract - Dropdown from Contracts */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Buy Contract
              </label>
              <div className="relative">
                <select
                  name="buyContractId"
                  value={formData.buyContractId}
                  onChange={(e) => handleContractChange('buyContractId', e.target.value)}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 300,
                    fontSize: '12px',
                    lineHeight: '20px',
                    height: '40px',
                    color: formData.buyContractId ? '#212126' : '#69697A',
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  <option value="">Select buy contract</option>
                  {buyContracts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.contractNumber} — {c.title}
                    </option>
                  ))}
                </select>
                <FiChevronDown 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ width: '16px', height: '16px', color: '#69697A' }}
                />
              </div>
            </div>
            {/* Sale Contract - Dropdown from Contracts */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Sale Contract
              </label>
              <div className="relative">
                <select
                  name="saleContractId"
                  value={formData.saleContractId}
                  onChange={(e) => handleContractChange('saleContractId', e.target.value)}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 300,
                    fontSize: '12px',
                    lineHeight: '20px',
                    height: '40px',
                    color: formData.saleContractId ? '#212126' : '#69697A',
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  <option value="">Select sale contract</option>
                  {saleContracts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.contractNumber} — {c.title}
                    </option>
                  ))}
                </select>
                <FiChevronDown 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ width: '16px', height: '16px', color: '#69697A' }}
                />
              </div>
            </div>
            {/* Booking */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Booking
              </label>
              <StockReferenceSelectInput
                type="booking"
                value={formData.booking}
                onChange={(booking) => setFormData((prev) => ({ ...prev, booking }))}
              />
            </div>
            {/* Bill of Lading */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Bill of Lading
              </label>
              <input
                type="text"
                name="billOfLading"
                value={formData.billOfLading}
                onChange={handleChange}
                placeholder="Enter bill of lading number"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontSize: '12px',
                  lineHeight: '20px',
                  height: '40px',
                  color: formData.billOfLading ? '#212126' : '#69697A'
                }}
              />
            </div>
            {/* Container Number(s) */}
            <div className="md:col-span-2">
              <label
                className="flex items-center gap-2 mb-3 cursor-pointer"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#69697A',
                }}
              >
                <input
                  type="checkbox"
                  checked={multiContainerMode}
                  onChange={(e) => {
                    setMultiContainerMode(e.target.checked)
                    setSubmitError(null)
                  }}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Multiple containers (same booking)
              </label>

              {multiContainerMode ? (
                <div className="space-y-3">
                  <div>
                    <label
                      className="block mb-2"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '22px',
                        color: '#69697A',
                      }}
                    >
                      Number of containers
                    </label>
                    <input
                      type="number"
                      min={2}
                      max={30}
                      value={containerCount}
                      onChange={(e) => updateContainerCount(parseInt(e.target.value, 10) || 2)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 300,
                        fontSize: '12px',
                        lineHeight: '20px',
                        height: '40px',
                        color: '#212126',
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {containerNumbers.map((cn, index) => (
                      <div key={index}>
                        <label
                          className="block mb-2"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 500,
                            fontSize: '14px',
                            lineHeight: '22px',
                            color: '#69697A',
                          }}
                        >
                          Container {index + 1}
                        </label>
                        <input
                          type="text"
                          value={cn}
                          onChange={(e) => handleContainerNumberChange(index, e.target.value)}
                          placeholder={`Enter container ${index + 1}`}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 300,
                            fontSize: '12px',
                            lineHeight: '20px',
                            height: '40px',
                            color: cn ? '#212126' : '#69697A',
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <p
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 300,
                      fontSize: '12px',
                      lineHeight: '18px',
                      color: '#69697A',
                    }}
                  >
                    Creates one shipment row per container, all sharing the same booking reference.
                  </p>
                </div>
              ) : (
                <div>
                  <label
                    className="block mb-2"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 500,
                      fontStyle: 'normal',
                      fontSize: '14px',
                      lineHeight: '22px',
                      letterSpacing: '0px',
                      color: '#69697A',
                    }}
                  >
                    Container Number
                  </label>
                  <input
                    type="text"
                    name="containerNumber"
                    value={formData.containerNumber}
                    onChange={handleChange}
                    placeholder="Enter container number"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 300,
                      fontSize: '12px',
                      lineHeight: '20px',
                      height: '40px',
                      color: formData.containerNumber ? '#212126' : '#69697A',
                    }}
                  />
                </div>
              )}
            </div>
            {/* Vessel */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Vessel
              </label>
              <input
                type="text"
                name="vessel"
                value={formData.vessel}
                onChange={handleChange}
                placeholder="Enter vessel name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontSize: '12px',
                  lineHeight: '20px',
                  height: '40px',
                  color: formData.vessel ? '#212126' : '#69697A'
                }}
              />
            </div>
            {/* Shipping Line — integrated carriers only (backend adapters) */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Shipping Line
              </label>
              <div className="relative">
                <select
                  name="shippingLine"
                  value={formData.shippingLine}
                  onChange={handleChange}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 300,
                    fontSize: '12px',
                    lineHeight: '20px',
                    height: '40px',
                    color: formData.shippingLine ? '#212126' : '#69697A',
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <option value="">Select shipping line</option>
                  {INTEGRATED_SHIPPING_LINE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <FiChevronDown
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ width: '16px', height: '16px', color: '#69697A' }}
                />
              </div>
            </div>
            {/* Product */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Product
              </label>
              <ProductSelectInput
                value={formData.product}
                onChange={(product) => setFormData((prev) => ({ ...prev, product }))}
              />
            </div>
            {/* Customer */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Customer
              </label>
              <CustomerSelectInput
                value={formData.customer}
                onChange={(customer) => setFormData((prev) => ({ ...prev, customer }))}
              />
            </div>
            {/* Factory */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Factory
              </label>
              <input
                type="text"
                name="factory"
                value={formData.factory}
                onChange={handleChange}
                placeholder="Enter factory name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontSize: '12px',
                  lineHeight: '20px',
                  height: '40px',
                  color: formData.factory ? '#212126' : '#69697A'
                }}
              />
            </div>
            {/* Origin */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Origin
              </label>
              <input
                type="text"
                name="origin"
                value={formData.origin}
                onChange={handleChange}
                placeholder="Enter origin"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontSize: '12px',
                  lineHeight: '20px',
                  height: '40px',
                  color: formData.origin ? '#212126' : '#69697A',
                  backgroundColor: '#FFFFFF'
                }}
              />
            </div>
            {/* Destination */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Destination
              </label>
              <input
                type="text"
                name="destination"
                value={formData.destination}
                onChange={handleChange}
                placeholder="Enter destination"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontSize: '12px',
                  lineHeight: '20px',
                  height: '40px',
                  color: formData.destination ? '#212126' : '#69697A',
                  backgroundColor: '#FFFFFF'
                }}
              />
            </div>
            {/* ETD */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                ETD (departure)
              </label>
              <p style={{ fontFamily: 'Lexend Deca', fontSize: '11px', lineHeight: '16px', color: '#9B9BA8', marginBottom: '6px' }}>
                Estimated departure — enter manually or sync from carrier tracking.
              </p>
              <div className="relative">
                <input
                  type="date"
                  name="etd"
                  value={formData.etd}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 300,
                    fontSize: '12px',
                    lineHeight: '20px',
                    height: '40px',
                    color: formData.etd ? '#212126' : '#69697A'
                  }}
                />
              </div>
            </div>
            {/* ETA */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                ETA
              </label>
              <div className="relative">
                <input
                  type="date"
                  name="eta"
                  value={formData.eta}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 300,
                    fontSize: '12px',
                    lineHeight: '20px',
                    height: '40px',
                    color: formData.eta ? '#212126' : '#69697A'
                  }}
                />
              </div>
            </div>
            {/* Status */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Status
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 300,
                    fontSize: '12px',
                    lineHeight: '20px',
                    height: '40px',
                    color: formData.status ? '#212126' : '#69697A',
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  <option value="">Select status</option>
                  {SHIPMENT_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <FiChevronDown 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{
                    width: '16px',
                    height: '16px',
                    color: '#69697A'
                  }}
                />
              </div>
            </div>
            {/* Shipping Documents */}
            <div>
              <label 
                className="block mb-2"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A'
                }}
              >
                Shipping Documents
              </label>
              <input
                ref={shippingFileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  setShippingDocumentFile(f || null)
                  setSubmitError(null)
                }}
              />
              <button
                type="button"
                onClick={() => shippingFileInputRef.current?.click()}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-center hover:opacity-90 transition-opacity cursor-pointer"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  height: '40px',
                  color: '#212126',
                  backgroundColor: '#FFFFFF'
                }}
              >
                {shippingDocumentFile ? shippingDocumentFile.name : 'Upload'}
              </button>
              {shippingDocumentFile && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setShippingDocumentFile(null) }}
                  className="mt-1 text-xs text-gray-500 hover:text-red-600 cursor-pointer"
                  style={{ fontFamily: 'Lexend Deca' }}
                >
                  Remove file
                </button>
              )}
            </div>
          </div>
        </form>

        {/* Action Bar */}
        <div 
          className="flex items-center justify-end gap-4 border-t border-gray-200 flex-shrink-0 px-4 sm:px-6"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2'
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#212126',
              border: '1px solid #212126',
              backgroundColor: '#FFFFFF',
              height: '38px'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="new-shipment-form"
            disabled={submitting}
            className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#FFFFFF',
              backgroundColor: '#212126',
              border: 'none',
              height: '38px'
            }}
          >
            {submitting ? 'Saving…' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default NewShipmentModal
